import { FunctionComponent, useContext, useEffect, useState } from 'react'
import { Box, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, FormControlLabel, FormHelperText, InputLabel, OutlinedInput, Stack, TextField, Typography } from '@mui/material'
import { useFormik } from 'formik';
import * as Yup from 'yup';
import ReportPeriodService from '../../../../shared/services/report-period/report-period.service';
import LocationService from '../../../../shared/services/location/location.service';
import { ReportPeriodContext } from '../../../../context/report-periods/report-periods';
import { ILocation } from '../../../../shared/services/location/location.model';
import { LoadingButton } from '@mui/lab';
import { DialogSaveButtons } from '../../../presentation-components/miscellaneous-components/dialog-save-buttons';

export const CloseReportPeriodDialog: FunctionComponent<{
  open: boolean,
  reportPeriodId: string,
  closeHandler: () => void,
  getReportPeriodDetails: () => void,
}> = ({
  open,
  reportPeriodId,
  closeHandler,
  getReportPeriodDetails,
}) => {

    const reportPeriodService = new ReportPeriodService();
    const locationService = LocationService.getInstance();
    const reportPeriodContext = useContext(ReportPeriodContext);
    const [locations, setLocations] = useState<ILocation[]>()
    const [errorMessage, setErrorMessage] = useState<string>()
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false)

    const closeReportPeriodSchema = Yup.object().shape({
      totalRevenue: Yup.number().min(0).integer('Your total revenue must be a whole number').required('You must provide your total revenue'),
      // emissionOffsetTonnes: Yup.number(),
      numberOfEmployees: Yup.number(),
      generateNewReportPeriod: Yup.boolean(),
    })

    const formik = useFormik({
      initialValues: {
        // emissionOffsetTonnes: undefined,
        totalRevenue: undefined,
        numberOfEmployees: null,
        generateNewReportPeriod: true,
        locationEmployees: [],
      },
      validationSchema: closeReportPeriodSchema,
      onSubmit: (values) => {
        if (!isSubmitting) {
          setIsSubmitting(true)
          reportPeriodService.closeReportPeriod(reportPeriodId, values)
            .then(() => {
              reportPeriodContext.forceUpdateReportPeriods()
              closeHandler();
              formik.resetForm();
              setIsSubmitting(false)
              getReportPeriodDetails()
            })
            .catch(err => {
              setErrorMessage(err.response.data.err.message)
              console.log(err)
              setIsSubmitting(false)
            })
        }
      }
    });

    useEffect(() => {
      if (open && formik.values.totalRevenue === undefined) {
        reportPeriodService.getReportPeriodorArchivedReportPeriod(reportPeriodId)
          .then(reportPeriod => {
            if (reportPeriod.totalRevenue || reportPeriod.totalRevenue === 0) {
              formik.setFieldValue('totalRevenue', reportPeriod.totalRevenue);
            }
          })
          .catch(err => {
            console.log(err);
          });
      }
    }, [open, reportPeriodId]);

    useEffect(() => {
      locationService.getAllLocations()
        .then(response => {
          setLocations(response)
          const totalNumberOfEmployees = response.map(location => location.numberOfEmployees).reduce((a, c) => a + c, 0)
          formik.setFieldValue('numberOfEmployees', totalNumberOfEmployees)
        })
        .catch(err => {
          console.log(err)
        })
    }, [])

    useEffect(() => {
      const totalNumberOfEmployees = locations?.map(location => location.numberOfEmployees).reduce((a, c) => a + c, 0)
      formik.setFieldValue('numberOfEmployees', totalNumberOfEmployees)

      const formattedData = locations?.map(location => {
        return {
          locationId: location.id,
          countryCode: location.countryId,
          numberOfEmployees: location.numberOfEmployees
        }
      })
      formik.setFieldValue('locationEmployees', formattedData)

    }, [locations])

    const changeHandler = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, locationId: string) => {
      const { value } = e.target;
      setLocations(locations?.map(location =>
        location.id === locationId ? { ...location, numberOfEmployees: parseInt(value) } : location
      )
      );
    };

    const cancelHandler = () => {
      formik.resetForm();
      closeHandler();
    }

    return (
      <Dialog open={open} onClose={closeHandler} maxWidth='xs'>
        <Box sx={{ p: 4 }}>
          <DialogTitle sx={{ pt: 0, pl: 0, pr: 0 }}>
            Close Report Period
          </DialogTitle>
          <DialogContent sx={{ pt: 0, pl: 0, pr: 0 }}>
            <Typography variant='body1'>Are you sure you want to close this report period? Once closed, no further changes will be possible.</Typography>
            <FormControl fullWidth variant='outlined' sx={{ mt: 2 }}>
              <InputLabel htmlFor='totalRevenue' >Total Revenue in £</InputLabel>
              <OutlinedInput id='totalRevenue' name='totalRevenue' type='number' label='total revenue in £ *'
                value={formik.values.totalRevenue !== undefined ? formik.values.totalRevenue : ''}
                onChange={formik.handleChange}
                inputProps={{ min: 0.00, step: 1 }}
                error={formik.touched.totalRevenue && Boolean(formik.errors.totalRevenue)}
              ></OutlinedInput>
              <Stack justifyContent='start' alignItems='start'>
                {formik.touched.totalRevenue && formik.errors.totalRevenue &&
                  <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>{formik.errors.totalRevenue}</FormHelperText>
                }
                {/* <FormHelperText sx={{ color: 'text.secondary', marginLeft: "16px !important" }}>The currency of the country where your organisation is headquatered will be used </FormHelperText> */}
              </Stack>
            </FormControl>
            {/* <FormControl fullWidth variant='outlined' sx={{ mt: 2 }}>
              <InputLabel htmlFor='emissionOffsetTonnes' >Number of carbon credits or offsets (tCO<sub>2</sub>e)</InputLabel>
              <OutlinedInput id='emissionOffsetTonnes' name='emissionOffsetTonnes' type='number' label='Number of  carbon credits or offsets (tCO2e)'
                value={formik.values.emissionOffsetTonnes || ''}
                onChange={formik.handleChange}
                inputProps={{ min: 0, step: 0.01 }}
                error={formik.touched.emissionOffsetTonnes && Boolean(formik.errors.emissionOffsetTonnes)}
              ></OutlinedInput>
              <Stack justifyContent='start' alignItems='start'>
                {formik.touched.emissionOffsetTonnes && formik.errors.emissionOffsetTonnes &&
                  <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>{formik.errors.emissionOffsetTonnes}</FormHelperText>
                }
              </Stack>
            </FormControl> */}
            {locations && locations.length > 0 && <Typography sx={{ mt: 2 }} variant='subtitle2'>Please confirm the number of employees at your location(s)</Typography>}
            {locations?.map((location, index) => {
              return (<TextField
                key={location.id}
                id='numberOfEmployees'
                name='numberOfEmployees'
                label={`${location.friendlyName}, ${location.country.name}`}
                type='number'
                fullWidth
                value={location.numberOfEmployees === null ? 0 : location.numberOfEmployees}
                sx={{ mt: 2 }}
                onChange={(e) => changeHandler(e, location.id)}
              />
              )
            }
            )}
            <FormControl sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', mt: 1 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formik.values.generateNewReportPeriod}
                    onChange={(event) => formik.setFieldValue('generateNewReportPeriod', event.target.checked)}
                  />
                }
                label={
                  <Typography variant='body1'>Automatically generate a new report period</Typography>
                }
              />
            </FormControl>
            {errorMessage &&
              <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>{errorMessage}
              </FormHelperText>
            }
          </DialogContent>
          <DialogActions sx={{ p: 0 }}>
            <DialogSaveButtons
              cancelHandler={cancelHandler}
              submitHandler={formik.submitForm}
              isSubmitting={isSubmitting}
            />
          </DialogActions>
        </Box>
      </Dialog>
    )
  }